import BookCover from "@components/book-cover";
import {IssueRenderProps} from "@components/grid/IssueGrid";
import {
    AddIcon,
    DiamondIcon,
    DollarIcon,
    FlameIcon,
    StorageBoxOutlineIcon,
    WishlistIcon
} from "@components/icons/Icons";
import {KeyIssueBadge} from "@components/key-issue-badge";
import useIsLoggedIn from "@hooks/useIsLoggedIn";
import {IssueActions, IssueGradeTypes, QueryParms, Routes} from "@lib/constants";
import {showResponse} from "@services/Api";
import {useWishlistAddIssueMutation, useWishlistRemoveIssueMutation} from "@mwServices/User";
import {Button, Checkbox, Col, Row, Space, Tooltip} from "antd";
import moment from "moment";
import React, {useEffect, useMemo} from "react";
import {Link} from "react-router-dom";
import {formatPercent} from "@lib/format";
import {FmvPrice} from "@components/fmv-price";
import {getMyCollectionUrlWithIssueId} from "@lib/Routes";

export interface IssuesGridItemProps extends IssueRenderProps {
    showValueRibbon?: boolean;
    showDesktopView?: boolean;
    windowDimensions: any;
    showEditMultiple?: boolean;
    isSelected?: boolean;
    toggleSelected?: (issueId: number) => void;
    setAddIssueId: (issueId: number) => void;
}

const IssuesGridItem = ({
    item,
    onIssueUpdate,
    showValueRibbon,
    showDesktopView,
    windowDimensions,
    showEditMultiple,
    isSelected,
    toggleSelected,
    setAddIssueId,
}: IssuesGridItemProps) => {
    const isLoggedIn = useIsLoggedIn();
    const issueDate = useMemo(() =>  moment(item.date).toDate(), [item]);
    const [wishlistIssueAdd, addWishlistResponse] = useWishlistAddIssueMutation();
    const [wishlistIssueRemove, removeWishlistResponse] = useWishlistRemoveIssueMutation();

    let gradedOrRawUpperCase = useMemo(() => item.graded_or_raw.toUpperCase(), [item]);
    const gradedOrSlab = useMemo(() => gradedOrRawUpperCase == IssueGradeTypes.GRADED ? IssueGradeTypes.SLAB : gradedOrRawUpperCase, [gradedOrRawUpperCase]);

    useEffect(() => {
        if (addWishlistResponse.isSuccess && onIssueUpdate) onIssueUpdate({ ...item, in_wishlist: true });
        showResponse(addWishlistResponse, IssueActions.WISHLIST.RESPONSE.ADD, IssueActions.WISHLIST.ERROR.ADD);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addWishlistResponse]);

    useEffect(() => {
        if (removeWishlistResponse.isSuccess && onIssueUpdate) onIssueUpdate({ ...item, in_wishlist: false });
        showResponse(removeWishlistResponse, IssueActions.WISHLIST.RESPONSE.REMOVE, IssueActions.WISHLIST.ERROR.REMOVE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeWishlistResponse]);

    return (
        <>
            <div className="covr-grid-header">
                <Row justify="space-between">
                    {(showEditMultiple && toggleSelected) && (
                        <Col span={1} className="col-cbx">
                            <Checkbox checked={isSelected} onChange={() => toggleSelected(item.id)}/>
                        </Col>
                    )}
                    <span className="min-height-24"></span>
                    {item.total_in_collection > 0 && (
                        <Link to={getMyCollectionUrlWithIssueId(item.id)}>
                            <span className="issues-owned">
                                <StorageBoxOutlineIcon/>
                                <strong>
                                    &nbsp;:&nbsp;<span className="total-number">{item.total_in_collection}</span>
                                </strong>
                            </span>
                        </Link>
                    )}
                </Row>
            </div>
            <div className="covr-grid-content">
                <a href={`${Routes.ISSUE}/${item.slug}`}>
                    <BookCover issue={item} preview={false} text={item.variant_name}/>
                </a>
            </div>
            <div className="covr-grid-footer">
                <Row justify="center">
                    <Col span={24}>
                        <Row
                            className="issue-grid-book-cover-value-white"
                            style={{borderBottomWidth: showValueRibbon ? 0 : 1}}
                        >
                            <Col span={12}>
                                <span className="issueNumber">
                                    {item.issue_number}
                                </span>
                                <span className="issueYear">
                                    ({issueDate.getFullYear()})
                                </span>
                            </Col>
                            <Col span={12} className="valueBadges">
                                {item.is_key && (
                                    <KeyIssueBadge
                                        keyInfo={item.key_info
                                            ? item.key_info
                                            : `No Description Found!`}
                                        windowDimensions={windowDimensions}
                                        isLargeScreen={showDesktopView}
                                    />
                                )}
                                {item.is_hot && (
                                    <div className="valueBadge hotIssue isActive">
                                        <FlameIcon/>
                                    </div>
                                )}
                                {item.is_rare && (
                                    <div className="valueBadge rareIssue isActive">
                                        <DiamondIcon/>
                                    </div>)}
                            </Col>
                        </Row>

                        {showValueRibbon && (
                            <>
                                <Row
                                    justify={`space-between`}
                                    className={`issue-grid-book-cover-value ${((gradedOrSlab == IssueGradeTypes.SLAB) ? 'blue ' : 'grey ')}`}
                                >
                                    <Col span={18}>
                                        <span
                                            className="issueGrade">{gradedOrSlab} {item.most_common_grade}&nbsp;|&nbsp;</span>
                                        <FmvPrice value={item.value} stale={false}/>
                                    </Col>
                                    {item.trend_percent !== null && item.trend_percent !== 0 && (
                                        <Col span={6}
                                             className={"valueChange " + (item.trend_percent && item.trend_percent >= 0 ? 'up' : 'down')}>
                                            {formatPercent(item.trend_percent, true)}
                                        </Col>
                                    )}
                                </Row>
                            </>
                        )}

                        <Row className="issue-grid-book-cover-details">
                            <p className="title" title={item.title}>{item.title}</p>
                        </Row>

                        {isLoggedIn && (
                            <Row className="issue-actions mar-top-10" justify="center">
                                <Space>
                                    <Tooltip title={!item.in_wishlist
                                        ? IssueActions.WISHLIST.ADD
                                        : IssueActions.WISHLIST.REMOVE}
                                    >
                                        <Button className={`issueButton greyButton ${(item.in_wishlist
                                            ? 'selected'
                                            : '')}`}
                                                shape="circle"
                                                onClick={() => {
                                                    if (item.in_wishlist) {
                                                        wishlistIssueRemove([item.id]);
                                                    } else {
                                                        wishlistIssueAdd([item.id]);
                                                    }
                                                }}>
                                            <WishlistIcon/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={IssueActions.COLLECTION.ADD}>
                                        <Button
                                            className="issueButton greyButton"
                                            shape="circle" size="large"
                                            onClick={() => {
                                                setAddIssueId(item.id);
                                            }}>
                                            <AddIcon/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={IssueActions.MARKETPLACE.BUY}>
                                        <a href={`${Routes.MARKETPLACE}/?${QueryParms.ISSUE_ID}=${item.id}`}>
                                            <Button
                                                className="issueButton greyButton"
                                                shape="circle">
                                                <DollarIcon className="dollar"/>
                                            </Button>
                                        </a>
                                    </Tooltip>
                                </Space>
                            </Row>
                        )}
                    </Col>
                </Row>
                <div className="select-arrow"/>
            </div>
        </>
    );
};

export default IssuesGridItem;